import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Mail, Phone, Instagram, MessageCircle,
    Send, ArrowUpRight,
} from "lucide-react";
import { BrandButton } from "../../components/ui/index.js";
import {
    transformLogoContactMobile,
    transformLogoContactTablet,
    transformLogoContactDesktop,
} from "../../components/HomeBackground/systems/constants.js";
import { showAlert, showErrorAlert } from "../../components/HelperAlerts/HelpersAlerts.jsx";

const WHATSAPP_NUMBER = "+34 600 000 000"; // placeholder
const WHATSAPP_LINK   = "https://wa.me/34600000000";
const INSTAGRAM_HANDLE = "@customiz3d.store";
const INSTAGRAM_LINK   = "https://www.instagram.com/customiz3d.store/";

// ─── Social pill ─────────────────────────────────────────────────────────────
function SocialLink({ href, icon, label, sublabel, color }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`
                group glassCard rounded-2xl p-4 flex items-center gap-4
                hover:scale-[1.02] transition-transform duration-200
            `}
        >
            <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                {icon}
            </span>
            <div className="flex-1 min-w-0">
                <p className="font-bold text-sm dark:text-white text-gray-900">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{sublabel}</p>
            </div>
            <ArrowUpRight size={16} className="text-gray-400 group-hover:text-primary transition-colors" />
        </a>
    );
}

// ─── CONTACT PAGE ─────────────────────────────────────────────────────────────
export default function Contact() {
    const [fields, setFields] = useState({ name: "", email: "", message: "" });
    const [sending, setSending] = useState(false);
    const asideRef = React.useRef(null);

    useEffect(() => { document.title = "Contacto — Customiz3D"; }, []);

    // ── Drive 3D logo extrusion from aside scroll ──────────────────────
    //
    // Física: ExtrudeGeometry centra la extrusión en Z local.
    // Tras rotar rotationX = α, el eje Z se proyecta en world Y con factor sin(α).
    // Para que la BASE (placa de impresión) quede fija mientras las capas
    // crecen hacia arriba, compensamos:
    //
    //   posY = posY₀ + s   in(α) × (e − e₀) / 2
    //
    // · Desktop  (α = π/2 , sin=1)    → compensación total en Y
    // · Tablet   (α = −0.4, sin≈−0.39) → compensación parcial
    // · Mobile   (α = 0   , sin=0)    → sin comp. Y (extrusión en Z, hacia cámara)
    //
    useEffect(() => {
        const aside = asideRef.current;
        if (!aside) return;

        const getDeviceData = () => {
            const w = window.innerWidth;
            if (w < 768)  return transformLogoContactMobile["section-0"];
            if (w <= 1024) return transformLogoContactTablet["section-0"];
            return transformLogoContactDesktop["section-0"];
        };

        const onScroll = () => {
            const d = getDeviceData();
            const progress = Math.min(
                aside.scrollTop / (aside.scrollHeight - aside.clientHeight || 1),
                1
            );

            // Extrusión: crece del constant al tope (maxExtrusion o 90 por defecto)
            const extStart = d.extrusion_depth ?? 2;
            const extEnd   = d.maxExtrusion ?? 90;
            const extrusion = extStart + progress * (extEnd - extStart);

            // Compensación positionY — mantiene la base fija según el ángulo de visión
            const rotX = d.rotationX ?? 0;
            const positionY = (d.positionY ?? 0) + Math.sin(rotX) * (extrusion - extStart) / 2;

            window.dispatchEvent(new CustomEvent('customiz3d:contact-scroll', {
                detail: { extrusion_depth: extrusion, positionY },
            }));
        };
        aside.addEventListener('scroll', onScroll);
        return () => aside.removeEventListener('scroll', onScroll);
    }, []);

    const handleChange = (e) =>
        setFields((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post("/contact", {
                name: fields.name,
                email: fields.email,
                content: fields.message,
            });
            showAlert({
                icon: "success",
                title: "¡Mensaje enviado!",
                text: "Te respondemos lo antes posible.",
                showConfirmButton: false,
                timer: 2000,
            });
            setFields({ name: "", email: "", message: "" });
        } catch {
            showErrorAlert({
                title: "Error al enviar",
                text: "Inténtalo de nuevo más tarde.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="flex h-full w-full overflow-hidden text-gray-900 dark:text-gray-200">

            {/* ── Columna izquierda: aside + franja logo (móvil) ─────────── */}
            <div className="flex flex-col w-full md:w-[48%] h-full">

            {/* ── LEFT ASIDE ─────────────────────────────────────────────── */}
            <aside ref={asideRef} className="
                flex-1
                overflow-y-auto overflow-x-hidden
                dark:bg-base-300/70 bg-white/75 backdrop-blur-md
                border-r border-white/10 dark:border-white/5
                flex flex-col
            ">
                {/* Header */}
                <div className="px-8 md:px-12 pt-14 pb-8 border-b border-base-300/30">
                    <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold mb-4">
                        Customiz3D — Contacto
                    </p>
                    <h1 className="text-5xl md:text-6xl font-black leading-[1.05] dark:text-white text-gray-900 mb-5">
                        Hablemos<br />
                        de tu <span className="text-brand-red">proyecto</span>.
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm leading-relaxed">
                        Cuéntanos tu idea y te respondemos en menos de 24 h.
                        Sin compromiso, sin formularios eternos.
                    </p>
                    <div className="mt-6 w-8 h-[2px] bg-brand-red" />
                </div>

                {/* Contact channels */}
                <div className="px-8 md:px-12 py-8 border-b border-base-300/30 flex flex-col gap-3">
                    <p className="text-xs uppercase tracking-widest text-primary/60 font-semibold mb-1">
                        Canales directos
                    </p>

                    <SocialLink
                        href={WHATSAPP_LINK}
                        icon={<MessageCircle size={20} className="text-white" />}
                        label="WhatsApp"
                        sublabel={WHATSAPP_NUMBER}
                        color="bg-emerald-500"
                    />
                    <SocialLink
                        href={INSTAGRAM_LINK}
                        icon={<Instagram size={20} className="text-white" />}
                        label="Instagram"
                        sublabel={INSTAGRAM_HANDLE}
                        color="bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500"
                    />
                    <a
                        href={`mailto:hola@customiz3d.com`}
                        className="group glassCard rounded-2xl p-4 flex items-center gap-4 hover:scale-[1.02] transition-transform duration-200"
                    >
                        <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary dark:bg-primary">
                            <Mail size={20} className="text-primary-content" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm dark:text-white text-gray-900">Email</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">hola@customiz3d.com</p>
                        </div>
                        <ArrowUpRight size={16} className="text-gray-400 group-hover:text-primary transition-colors" />
                    </a>
                    <div className="glassCard rounded-2xl p-4 flex items-center gap-4">
                        <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-accent/20">
                            <Phone size={20} className="text-accent" />
                        </span>
                        <div>
                            <p className="font-bold text-sm dark:text-white text-gray-900">Teléfono</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{WHATSAPP_NUMBER}</p>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <div className="px-8 md:px-12 py-8 flex-1">
                    <p className="text-xs uppercase tracking-widest text-primary/60 font-semibold mb-5">
                        O déjanos un mensaje
                    </p>
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                                    Nombre
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={fields.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="Tu nombre"
                                    className="
                                        w-full rounded-xl px-4 py-2.5 text-sm
                                        bg-base-200/60 dark:bg-base-200/40
                                        border border-base-300/50 dark:border-white/10
                                        text-gray-900 dark:text-white
                                        placeholder-gray-400 dark:placeholder-gray-600
                                        focus:outline-none focus:ring-2 focus:ring-brand-red/40
                                        transition
                                    "
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={fields.email}
                                    onChange={handleChange}
                                    required
                                    placeholder="tu@email.com"
                                    className="
                                        w-full rounded-xl px-4 py-2.5 text-sm
                                        bg-base-200/60 dark:bg-base-200/40
                                        border border-base-300/50 dark:border-white/10
                                        text-gray-900 dark:text-white
                                        placeholder-gray-400 dark:placeholder-gray-600
                                        focus:outline-none focus:ring-2 focus:ring-brand-red/40
                                        transition
                                    "
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                                Mensaje
                            </label>
                            <textarea
                                name="message"
                                value={fields.message}
                                onChange={handleChange}
                                required
                                rows={5}
                                placeholder="Cuéntanos tu proyecto, cantidad aproximada, materiales que te interesan…"
                                className="
                                    w-full rounded-xl px-4 py-3 text-sm
                                    bg-base-200/60 dark:bg-base-200/40
                                    border border-base-300/50 dark:border-white/10
                                    text-gray-900 dark:text-white
                                    placeholder-gray-400 dark:placeholder-gray-600
                                    focus:outline-none focus:ring-2 focus:ring-brand-red/40
                                    transition resize-none
                                "
                            />
                        </div>

                        <div className="flex items-center gap-4 pt-1">
                            <BrandButton
                                as="button"
                                type="submit"
                                size="md"
                                disabled={sending}
                                className="gap-2"
                            >
                                {sending ? "Enviando…" : "Enviar mensaje"}
                                {!sending && <Send size={15} />}
                            </BrandButton>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                                Sin spam. Nunca compartimos tu email.
                            </p>
                        </div>
                    </form>
                </div>

                {/* Footer note */}
                <div className="px-8 md:px-12 py-6 border-t border-base-300/30">
                    <p className="text-xs text-gray-400 dark:text-gray-600">
                        Horario de atención: Lun – Vie · 9 h a 18 h
                    </p>
                </div>
            </aside>

            {/* ── MÓVIL: franja transparente con logo abajo ──────────────── */}
            <div className="md:hidden flex-shrink-0 h-[32vh] relative pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-b from-black/25 to-transparent" />
            </div>

            </div>{/* fin columna izquierda */}

            {/* ── RIGHT — 3D background ────────────────────────────────── */}
            <div className="hidden md:flex flex-1 h-full relative bg-black/85 items-center justify-center">
                <div className="flex flex-col items-center gap-6 select-none pointer-events-none text-center">
                    <p className="text-xs uppercase tracking-[0.3em] text-primary/70 font-semibold">
                        Respondemos en
                    </p>
                    <span className="text-[7rem] font-black leading-none text-white/20">
                        &lt;24h
                    </span>
                    <div className="w-10 h-[2px] bg-brand-red/80" />
                    <p className="text-sm text-white/35 max-w-[200px] leading-snug">
                        Cada proyecto es único. Cada respuesta también.
                    </p>
                </div>
            </div>
        </div>
    );
}
